import React, { useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import type {
  IncidentActionSnapshotRollupResponse,
  IncidentEscalationRollupResponse,
  IncidentCorrectiveActionRollupResponse,
  IncidentGroup,
  IncidentMeasurementRollupResponse,
  IncidentOpsQueuesResponse,
  IncidentOwnershipRollupResponse,
  IncidentReviewOutcomeHistoryResponse,
  IncidentReviewOutcomeRollupResponse,
} from '../../services/incidentsApi';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import {
  getIncidentGroupPatternLabel,
  getIncidentGroupPatternSummary,
  getIncidentGroupPatternState,
  isIncidentGroupRecurring,
  isIncidentGroupRecurringHotspot,
} from '../../domain/incidents/incidentPatternSemantics';
import {
  buildOutcomeBoundarySummary,
  buildScopeBoundarySummary,
} from '../../domain/incidents/sourceContextSemantics';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import { ownerRoleIsUnassigned } from '../../domain/incidents/incidentPatternVocabulary';
import { deriveReviewerCaseState } from '../../domain/incidents/reviewerCaseState';
import { resolveCanonicalReviewPopulation } from '../../domain/incidents/reviewPopulationMetrics';
import { incidentCanonicalFollowupOwnerLabel } from './reviewCanonical';

type HotspotTarget = {
  key: string;
  type: 'zone' | 'camera';
  id: string;
  label: string;
  count: number;
  patternLabel?: string;
  patternSummary?: string;
  hotspotScore?: number;
};

export type HotspotsCardPresentation = 'card' | 'groupedDrilldown';

export interface HotspotsCardProps {
  groups: IncidentGroup[];
  zoneNameById: Map<string, string>;
  cameraNameById: Map<string, string>;
  onSelectHotspot: (target: { type: 'zone' | 'camera'; id: string }) => void;
  /** Investigate grouped below-table drill-down. Default keeps the Analytics card. */
  presentation?: HotspotsCardPresentation;
  tableMode?: 'grouped' | 'raw';
  selectedGroup?: IncidentGroup | null;
  reviewOutcomeRollup?: IncidentReviewOutcomeRollupResponse | null;
  reviewOutcomeHistory?: IncidentReviewOutcomeHistoryResponse | null;
  ownershipRollup?: IncidentOwnershipRollupResponse | null;
  correctiveActionRollup?: IncidentCorrectiveActionRollupResponse | null;
  measurementRollup?: IncidentMeasurementRollupResponse | null;
  actionSnapshotRollup?: IncidentActionSnapshotRollupResponse | null;
  escalationRollup?: IncidentEscalationRollupResponse | null;
  opsQueues?: IncidentOpsQueuesResponse | null;
}

function reviewOutcomeLabel(value: string): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'true_event':
      return 'Confirmed events';
    case 'false_positive':
      return 'False positives';
    case 'expected_activity':
      return 'Expected activity';
    case 'insufficient_evidence':
      return 'Insufficient evidence';
    case 'demo_or_test_activity':
      return 'Special-case activity';
    case 'duplicate':
      return 'Duplicates';
    case 'unknown':
      return 'Not reviewed yet';
    default:
      return raw.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  }
}

function ownershipStateLabel(value: string): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'unassigned':
      return `Needs ${displaySafetyManagerLabel('followup_owner').toLowerCase()}`;
    case 'assigned':
      return `${displaySafetyManagerLabel('followup_owner')} assigned`;
    case 'resolved':
      return 'Resolved';
    default:
      return raw.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  }
}

function correctiveActionStateLabel(value: string): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'not_started':
      return 'No work order yet';
    case 'linked':
      return 'Linked';
    case 'assigned':
      return 'Assigned';
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    default:
      return raw.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  }
}

function measurementStateLabel(value: string): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'needs_review':
      return 'Needs review first';
    case 'not_applicable':
      return 'Not applicable';
    case 'action_pending':
      return 'Action still open';
    case 'ready_for_followup':
      return `Ready for ${displaySafetyManagerLabel('followup_measurement').toLowerCase()}`;
    case 'measured':
      return 'Measured';
    default:
      return raw.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  }
}

function actionSnapshotStateLabel(value: string): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'no_action':
      return 'No action captured';
    case 'in_progress':
      return 'Action in progress';
    case 'followup_scheduled':
      return 'Action scheduled';
    case 'closed':
      return 'Closed';
    default:
      return raw.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  }
}

function selectedGroupRiskArea(
  group: IncidentGroup,
  zoneNameById: Map<string, string>,
  cameraNameById: Map<string, string>,
): string {
  const zoneId = String(group.zone_id || '').trim();
  if (zoneId) return presentAreaLabel(zoneNameById.get(zoneId) || zoneId);
  const cameraId = String(group.camera_id || '').trim();
  if (cameraId) {
    const cameraLabel = cameraNameById.get(cameraId) || cameraId;
    return /^camera\b/i.test(cameraLabel) ? cameraLabel : `Camera ${cameraLabel}`;
  }
  return 'Unknown area';
}

function selectedGroupActionOwner(group: IncidentGroup): string {
  const followup = incidentCanonicalFollowupOwnerLabel(group);
  if (followup && !ownerRoleIsUnassigned(followup)) return followup;
  return 'Unassigned';
}

function selectedGroupObservationCount(group: IncidentGroup): string {
  const count = Math.max(0, Number(group.alert_count || 0));
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'observation' : 'observations'}`;
}

function escalationStateLabel(value: string): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'clear':
      return 'No active escalation';
    case 'scheduled':
      return 'Action scheduled';
    case 'escalated':
      return 'Escalation active';
    case 'overdue':
      return 'Needs escalation attention';
    default:
      return raw.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
  }
}

const HotspotsCard: React.FC<HotspotsCardProps> = ({
  groups,
  zoneNameById,
  cameraNameById,
  onSelectHotspot,
  presentation = 'card',
  tableMode,
  selectedGroup = null,
  reviewOutcomeRollup,
  reviewOutcomeHistory,
  ownershipRollup,
  correctiveActionRollup,
  measurementRollup,
  actionSnapshotRollup,
  escalationRollup,
  opsQueues,
}) => {
  const groupedDrilldown = presentation === 'groupedDrilldown';
  const [riskDetailsExpanded, setRiskDetailsExpanded] = useState(false);
  const hotspots = useMemo(() => {
    const source = groups.filter((group) => {
      const state = getIncidentGroupPatternState(group);
      if (state === 'recurring_hotspot' || state === 'emerging_pattern') return true;
      return isIncidentGroupRecurring(group);
    });
    const items = source.length > 0 ? source : groups;
    const bucket = new Map<string, HotspotTarget>();

    items.forEach((group) => {
      const count = Math.max(1, Number(group.alert_count || 0));
      const hotspotScore = Math.max(
        count,
        Number(group?.risk_pattern?.hotspot_score || 0),
      );
      const resolvedPatternLabel = getIncidentGroupPatternLabel(group);
      const patternSummary = getIncidentGroupPatternSummary(group);
      const zoneId = String(group.zone_id || '').trim();
      const cameraId = String(group.camera_id || '').trim();
      if (zoneId) {
        const zoneLabel = presentAreaLabel(zoneNameById.get(zoneId) || zoneId);
        const key = `zone:${zoneId}`;
        const current = bucket.get(key) || {
          key,
          type: 'zone' as const,
          id: zoneId,
          label: zoneLabel,
          count: 0,
          hotspotScore: 0,
        };
        current.count += count;
        current.hotspotScore = Math.max(Number(current.hotspotScore || 0), hotspotScore);
        current.patternLabel = current.patternLabel || resolvedPatternLabel;
        current.patternSummary = current.patternSummary || patternSummary;
        bucket.set(key, current);
        return;
      }
      if (cameraId) {
        const cameraLabel = cameraNameById.get(cameraId) || cameraId;
        const key = `camera:${cameraId}`;
        const current = bucket.get(key) || {
          key,
          type: 'camera' as const,
          id: cameraId,
          label: /^camera\b/i.test(cameraLabel) ? cameraLabel : `Camera ${cameraLabel}`,
          count: 0,
          hotspotScore: 0,
        };
        current.count += count;
        current.hotspotScore = Math.max(Number(current.hotspotScore || 0), hotspotScore);
        current.patternLabel = current.patternLabel || resolvedPatternLabel;
        current.patternSummary = current.patternSummary || patternSummary;
        bucket.set(key, current);
      }
    });

    return Array.from(bucket.values())
      .sort((a, b) => {
        const scoreDiff = Number(b.hotspotScore || 0) - Number(a.hotspotScore || 0);
        if (scoreDiff !== 0) return scoreDiff;
        return b.count - a.count;
      })
      .slice(0, 3);
  }, [cameraNameById, groups, zoneNameById]);

  const repeatAreaCount = useMemo(() => {
    const source = groups.filter((g) => {
      return isIncidentGroupRecurringHotspot(g);
    });
    const keys = new Set(
      source
        .map((g) => (String(g.zone_id || '').trim() ? `z:${g.zone_id}` : String(g.camera_id || '').trim() ? `c:${g.camera_id}` : ''))
        .filter(Boolean),
    );
    return keys.size;
  }, [groups]);

  const helperLine = useMemo(() => {
    if (hotspots.length === 0) return 'No recurring risk patterns in this range.';
    if (repeatAreaCount <= 0) return 'Ranked by pattern observations. Select an area to filter the queue.';
    return `${repeatAreaCount} ${repeatAreaCount === 1 ? 'area shows' : 'areas show'} a recurring risk pattern. Select ${repeatAreaCount === 1 ? 'it' : 'an area'} to filter the queue.`;
  }, [hotspots.length, repeatAreaCount]);

  const recurrenceCaveat = useMemo(() => {
    if (hotspots.length <= 0) return '';
    return 'Pattern observations summarize repeated activity and should not be read as separate verified incidents.';
  }, [hotspots.length]);

  const reviewOutcomeItems = useMemo(() => {
    const items = Array.isArray(reviewOutcomeRollup?.items) ? (reviewOutcomeRollup?.items ?? []) : [];
    return items.filter((item) => Number(item?.count || 0) > 0).slice(0, 3);
  }, [reviewOutcomeRollup]);

  const reviewOutcomeLine = useMemo(() => {
    const confirmedRisk = Number(reviewOutcomeRollup?.customer_risk_total || 0);
    const uncertainRisk = Number(
      Array.isArray(reviewOutcomeRollup?.risk_disposition_items)
        ? ((reviewOutcomeRollup?.risk_disposition_items.find((item) => String(item?.risk_disposition || '') === 'uncertain_risk')?.count) || 0)
        : 0
    );
    const excludedRisk = Number(
      Array.isArray(reviewOutcomeRollup?.risk_disposition_items)
        ? ((reviewOutcomeRollup?.risk_disposition_items.find((item) => String(item?.risk_disposition || '') === 'excluded_from_risk')?.count) || 0)
        : 0
    );
    const reviewPopulation = resolveCanonicalReviewPopulation(reviewOutcomeRollup);
    const reviewed = reviewPopulation.reviewed;
    const total = reviewPopulation.total;
    if (total <= 0) return 'No pattern observations in this range yet.';
    if (reviewed <= 0) return `${total} pattern observations are in scope, but none have a recorded review status yet.`;
    if (!reviewPopulation.ratioAllowed) {
      return `${reviewed} pattern observations have a recorded review status in this range. ${confirmedRisk} currently count as confirmed safety concerns, ${uncertainRisk} remain evidence-limited, and ${excludedRisk} were reviewed outside the report scope.`;
    }
    return `${reviewed} of ${total} pattern observations have a recorded review status in this range. ${confirmedRisk} currently count as confirmed safety concerns, ${uncertainRisk} remain evidence-limited, and ${excludedRisk} were reviewed outside the report scope.`;
  }, [reviewOutcomeRollup]);

  const reviewOutcomeTrendLine = useMemo(() => {
    const history = Array.isArray(reviewOutcomeHistory?.history) ? (reviewOutcomeHistory?.history ?? []) : [];
    if (history.length <= 0) return '';
    const countDisposition = (bucket: any, disposition: string) =>
      Number(
        Array.isArray(bucket?.risk_disposition_items)
          ? ((bucket.risk_disposition_items.find((item: any) => String(item?.risk_disposition || '') === disposition)?.count) || 0)
          : 0,
      );
    const latest = history[history.length - 1];
    const previous = history.length > 1 ? history[history.length - 2] : null;
    const latestConfirmed = Number(latest?.customer_risk_total || 0);
    const latestUncertain = countDisposition(latest, 'uncertain_risk');
    const latestExcluded = countDisposition(latest, 'excluded_from_risk');
    if (!previous) {
      return `Latest review bucket shows ${latestConfirmed} confirmed safety concern${latestConfirmed === 1 ? '' : 's'}, ${latestUncertain} evidence-limited review${latestUncertain === 1 ? '' : 's'}, and ${latestExcluded} reviewed non-risk activit${latestExcluded === 1 ? 'y' : 'ies'}.`;
    }
    const prevConfirmed = Number(previous?.customer_risk_total || 0);
    const prevUncertain = countDisposition(previous, 'uncertain_risk');
    const prevExcluded = countDisposition(previous, 'excluded_from_risk');
    return `Trend: confirmed safety concerns moved from ${prevConfirmed} to ${latestConfirmed}; evidence-limited review moved from ${prevUncertain} to ${latestUncertain}; reviewed non-risk activity moved from ${prevExcluded} to ${latestExcluded}.`;
  }, [reviewOutcomeHistory]);
  const reviewBoundaryLine = useMemo(
    () => buildScopeBoundarySummary(reviewOutcomeRollup, 'pattern observations in this range'),
    [reviewOutcomeRollup],
  );

  const ownershipItems = useMemo(() => {
    const items = Array.isArray(ownershipRollup?.items) ? (ownershipRollup?.items ?? []) : [];
    return items.filter((item) => Number(item?.count || 0) > 0).slice(0, 3);
  }, [ownershipRollup]);

  const ownershipLine = useMemo(() => {
    const total = Number(ownershipRollup?.total_incidents || 0);
    const assigned = Number(ownershipRollup?.assigned_total || 0);
    const unassigned = Number(ownershipRollup?.unassigned_total || 0);
    if (total <= 0) return 'No action ownership data in this range yet.';
    if (assigned <= 0) return `${total} patterns are in scope and all still need an action owner.`;
    if (unassigned <= 0) return `${assigned} patterns already have an action owner in this range.`;
    return `${assigned} patterns have an action owner; ${unassigned} still need assignment.`;
  }, [ownershipRollup]);

  const correctiveActionItems = useMemo(() => {
    const items = Array.isArray(correctiveActionRollup?.items) ? (correctiveActionRollup?.items ?? []) : [];
    return items.filter((item) => Number(item?.count || 0) > 0).slice(0, 3);
  }, [correctiveActionRollup]);

  const correctiveActionLine = useMemo(() => {
    const total = Number(correctiveActionRollup?.total_incidents || 0);
    const workOrders = Number(correctiveActionRollup?.work_order_total || 0);
    const notStarted = Number(correctiveActionRollup?.not_started_total || 0);
    if (total <= 0) return 'No corrective-action data in this range yet.';
    if (workOrders <= 0) return `${total} patterns are in scope and none are linked to a work order yet.`;
    if (notStarted <= 0) return `${workOrders} patterns already have a linked corrective workflow in this range.`;
    return `${workOrders} patterns are linked to corrective work; ${notStarted} still have no linked work order.`;
  }, [correctiveActionRollup]);

  const measurementItems = useMemo(() => {
    const items = Array.isArray(measurementRollup?.items) ? (measurementRollup?.items ?? []) : [];
    return items.filter((item) => Number(item?.count || 0) > 0).slice(0, 3);
  }, [measurementRollup]);

  const measurementLine = useMemo(() => {
    const total = Number(measurementRollup?.total_incidents || 0);
    const applicable = Number(measurementRollup?.applicable_total || 0);
    const ready = Number(measurementRollup?.ready_total || 0);
    const measured = Number(measurementRollup?.measured_total || 0);
    if (total <= 0) return 'No impact-tracking data in this range yet.';
    if (applicable <= 0) return `${total} patterns are in scope, but none are ready to be measured yet.`;
    if (measured > 0) return `${measured} patterns already have recorded impact tracking; ${Math.max(0, applicable - measured)} remain in the readiness pipeline.`;
    if (ready > 0) return `${ready} patterns are ready for impact tracking once the next window is recorded.`;
    return `${applicable} patterns are on the impact-tracking path, but corrective closure or review still blocks impact tracking.`;
  }, [measurementRollup]);
  const measurementBoundaryLine = useMemo(
    () => buildOutcomeBoundarySummary(
      measurementRollup,
      Number(measurementRollup?.measured_total || 0),
      'this range',
    ),
    [measurementRollup],
  );

  const actionSnapshotItems = useMemo(() => {
    const items = Array.isArray(actionSnapshotRollup?.items) ? (actionSnapshotRollup?.items ?? []) : [];
    return items.filter((item) => Number(item?.count || 0) > 0).slice(0, 3);
  }, [actionSnapshotRollup]);

  const actionSnapshotLine = useMemo(() => {
    const total = Number(actionSnapshotRollup?.total_incidents || 0);
    const inProgress = Number(actionSnapshotRollup?.in_progress_total || 0);
    const scheduled = Number(actionSnapshotRollup?.scheduled_total || 0);
    const closed = Number(actionSnapshotRollup?.closed_total || 0);
    if (total <= 0) return 'No action workflow data in this range yet.';
    if (closed > 0) return `${closed} patterns have completed the action loop; ${scheduled} have action scheduled.`;
    if (scheduled > 0) return `${scheduled} patterns already have a next review time; ${Math.max(0, inProgress - scheduled)} are still in progress.`;
    if (inProgress > 0) return `${inProgress} patterns have action activity, but no next review time is recorded yet.`;
    return `${total} patterns are in scope and still have no captured action workflow.`;
  }, [actionSnapshotRollup]);

  const escalationItems = useMemo(() => {
    const items = Array.isArray(escalationRollup?.items) ? (escalationRollup?.items ?? []) : [];
    const normalized = items.filter((item) => Number(item?.count || 0) > 0).slice(0, 3);
    if (normalized.length > 0) return normalized;

    const overdue = Math.max(0, Number(opsQueues?.queues?.overdue?.count || 0));
    const escalations = Math.max(0, Number(opsQueues?.queues?.escalations?.count || 0));
    if (overdue <= 0 && escalations <= 0) return [];

    const fallback: Array<{ escalation_state: string; count: number }> = [];
    if (escalations > overdue) {
      fallback.push({ escalation_state: 'escalated', count: escalations - overdue });
    }
    if (overdue > 0) {
      fallback.push({ escalation_state: 'overdue', count: overdue });
    }
    return fallback.slice(0, 3);
  }, [escalationRollup, opsQueues]);

  const escalationLine = useMemo(() => {
    const total = Number(escalationRollup?.total_incidents || 0);
    const escalated = Number(escalationRollup?.escalated_total || 0);
    const overdue = Number(escalationRollup?.overdue_total || 0);
    const scheduled = Number(escalationRollup?.scheduled_total || 0);
    if (total <= 0) return 'No escalation data in this range yet.';
    if (escalated <= 0 && scheduled <= 0) return `${total} patterns are in scope and none have active escalation pressure yet.`;
    if (overdue > 0) return `${escalated} patterns need escalation attention; ${overdue} are already overdue.`;
    if (scheduled > 0 && escalated <= 0) return `${scheduled} patterns have action scheduled and no active escalation yet.`;
    return `${escalated} patterns need escalation attention in this range.`;
  }, [escalationRollup]);

  const escalationLineResolved = useMemo(() => {
    const total = Number(escalationRollup?.total_incidents || 0);
    if (total > 0) return escalationLine;

    const queueEscalated = Math.max(0, Number(opsQueues?.queues?.escalations?.count || 0));
    const queueOverdue = Math.max(0, Number(opsQueues?.queues?.overdue?.count || 0));
    if (queueEscalated <= 0 && queueOverdue <= 0) return escalationLine;
    if (queueOverdue > 0) return `${queueEscalated} patterns currently need escalation attention; ${queueOverdue} are already overdue.`;
    return `${queueEscalated} patterns currently need escalation attention in the review queue.`;
  }, [escalationLine, escalationRollup?.total_incidents, opsQueues]);

  const compactSummary = useMemo(() => {
    if (groupedDrilldown && selectedGroup) {
      return {
        riskArea: selectedGroupRiskArea(selectedGroup, zoneNameById, cameraNameById),
        rawSignals: selectedGroupObservationCount(selectedGroup),
        actionOwner: selectedGroupActionOwner(selectedGroup),
        nextStep: deriveReviewerCaseState(selectedGroup).label,
      };
    }
    const topHotspot = hotspots[0];
    const riskArea = topHotspot?.label || 'No recurring area in this range';
    const rawSignals = topHotspot
      ? `${topHotspot.count.toLocaleString('en-US')} ${topHotspot.count === 1 ? 'observation' : 'observations'}`
      : '—';
    return { riskArea, rawSignals, actionOwner: ownershipLine, nextStep: actionSnapshotLine };
  }, [
    actionSnapshotLine,
    cameraNameById,
    groupedDrilldown,
    hotspots,
    ownershipLine,
    selectedGroup,
    zoneNameById,
  ]);

  if (tableMode === 'raw') return null;
  if (groupedDrilldown && !selectedGroup) return null;

  const surfaceSx: SxProps<Theme> = groupedDrilldown
    ? {
        p: 0,
        borderRadius: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      }
    : {
        p: 1.4,
        borderRadius: 2.5,
        height: '100%',
        borderColor: alpha('#d97706', 0.2),
        bgcolor: alpha('#d97706', 0.03),
        display: 'flex',
        flexDirection: 'column',
      };

  const content = (
    <>
      <Typography sx={{ fontSize: groupedDrilldown ? 14 : 16, fontWeight: 700, lineHeight: 1.2 }}>
        {groupedDrilldown ? 'Recurrence' : 'Recurring Risk Patterns'}
      </Typography>
      {!groupedDrilldown ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, mb: 1, lineHeight: 1.35, fontSize: 11.5 }}>
          {helperLine}
        </Typography>
      ) : null}
      {!groupedDrilldown && recurrenceCaveat ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.35, fontSize: 11.5 }}>
          {recurrenceCaveat}
        </Typography>
      ) : null}

      <Box
        data-testid="recurring-risk-compact-summary"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: groupedDrilldown ? 1.25 : 1,
          mt: groupedDrilldown ? 1.25 : 0,
          mb: groupedDrilldown ? 0.5 : 1,
          p: groupedDrilldown ? 0 : 1,
          borderRadius: groupedDrilldown ? 0 : 1.5,
          border: groupedDrilldown ? 'none' : '1px solid',
          borderColor: groupedDrilldown ? 'transparent' : alpha('#e2e8f0', 0.95),
          bgcolor: groupedDrilldown ? 'transparent' : alpha('#ffffff', 0.9),
        }}
      >
        {[
          { label: 'Risk area', value: compactSummary.riskArea },
          { label: groupedDrilldown ? 'Observations' : 'Pattern observations', value: compactSummary.rawSignals },
          { label: displaySafetyManagerLabel('followup_owner'), value: compactSummary.actionOwner },
          { label: 'Next step', value: compactSummary.nextStep },
        ].map((row) => (
          <Box key={row.label} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontSize: 11 }}>
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: groupedDrilldown ? 600 : 700, fontSize: 13, lineHeight: 1.4 }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {groupedDrilldown ? null : (
      <Box sx={{ display: 'grid', gap: 0.65, flex: 1, alignContent: 'start' }}>
        {hotspots.length > 0 ? (
          hotspots.map((hotspot, index) => {
            const hotspotLabel = hotspot.label;
            const hotspotDetail = hotspot.patternLabel
              ? `${hotspot.patternLabel}${hotspot.patternSummary ? ` · ${hotspot.patternSummary}` : ''}`
              : '';
            const hotspotCountLabel = String(hotspot.count);
            return (
            <Tooltip key={hotspot.key} title="Filter the review queue to this recurring risk pattern" enterDelay={400}>
              <ButtonBase
                onClick={() => onSelectHotspot({ type: hotspot.type, id: hotspot.id })}
                sx={{
                  width: '100%',
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  px: 1,
                  py: 0.6,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                  '&:hover': {
                    backgroundColor: alpha('#ffedd5', 0.65),
                    borderColor: alpha('#d97706', 0.3),
                  },
                }}
              >
                <Box sx={{ minWidth: 0, pr: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }} noWrap>
                    {index + 1}. {hotspotLabel}
                  </Typography>
                  {hotspotDetail ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }} noWrap>
                      {hotspotDetail}
                    </Typography>
                  ) : null}
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#9a5800', flexShrink: 0, whiteSpace: 'nowrap' }} tabIndex={-1}>
                  {hotspotCountLabel}
                </Typography>
              </ButtonBase>
            </Tooltip>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            No recurring-risk pattern data for this range.
          </Typography>
        )}
      </Box>
      )}

      <Accordion
        expanded={riskDetailsExpanded}
        onChange={(_, expanded) => setRiskDetailsExpanded(expanded)}
        disableGutters
        elevation={0}
        data-testid="recurring-risk-details"
        sx={{
          mt: groupedDrilldown ? 0.5 : 1,
          border: groupedDrilldown ? 'none' : '1px solid',
          borderTop: groupedDrilldown ? '1px solid' : undefined,
          borderColor: groupedDrilldown ? 'divider' : alpha('#d97706', 0.18),
          borderRadius: groupedDrilldown ? 0 : 1.5,
          bgcolor: 'transparent',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: groupedDrilldown ? 0 : 1, minHeight: 40 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {groupedDrilldown ? 'Details' : 'Pattern Details'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
      <Box sx={{ pt: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>
          Review status rollup
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
          {reviewOutcomeLine}
        </Typography>
        {reviewOutcomeTrendLine ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
            {reviewOutcomeTrendLine}
          </Typography>
        ) : null}
        {reviewBoundaryLine ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
            {reviewBoundaryLine}
          </Typography>
        ) : null}
        {reviewOutcomeItems.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 0.55 }}>
            {reviewOutcomeItems.map((item) => (
              <Box
                key={`${item.review_outcome}:${item.count}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.55,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>
                  {reviewOutcomeLabel(item.review_outcome)}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#7c2d12', flexShrink: 0 }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Review status summaries will appear here once operators classify pattern observations in this range.
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 1.2, pt: 1.1, borderTop: '1px solid', borderColor: alpha('#d97706', 0.18) }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>
          Action ownership
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
          {ownershipLine}
        </Typography>
        {ownershipItems.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 0.55 }}>
            {ownershipItems.map((item) => (
              <Box
                key={`${item.ownership_state}:${item.count}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.55,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>
                  {ownershipStateLabel(item.ownership_state)}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#0f766e', flexShrink: 0 }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Action ownership will appear here once patterns in this range are assigned or resolved.
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 1.2, pt: 1.1, borderTop: '1px solid', borderColor: alpha('#d97706', 0.18) }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>
          Corrective action
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
          {correctiveActionLine}
        </Typography>
        {correctiveActionItems.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 0.55 }}>
            {correctiveActionItems.map((item) => (
              <Box
                key={`${item.corrective_action_state}:${item.count}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.55,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>
                  {correctiveActionStateLabel(item.corrective_action_state)}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#1d4ed8', flexShrink: 0 }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Corrective-action linkage will appear here once patterns are connected to work orders.
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 1.2, pt: 1.1, borderTop: '1px solid', borderColor: alpha('#d97706', 0.18) }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>
          Escalation status
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
          {escalationLineResolved}
        </Typography>
        {escalationItems.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 0.55 }}>
            {escalationItems.map((item) => (
              <Box
                key={`${item.escalation_state}:${item.count}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.55,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>
                  {escalationStateLabel(item.escalation_state)}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#b45309', flexShrink: 0 }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Escalation status will appear here once patterns in this range hit overdue or escalation conditions.
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 1.2, pt: 1.1, borderTop: '1px solid', borderColor: alpha('#d97706', 0.18) }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>
          {displaySafetyManagerLabel('followup_measurement')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
          {measurementLine}
        </Typography>
        {measurementBoundaryLine ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
            {measurementBoundaryLine}
          </Typography>
        ) : null}
        {measurementItems.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 0.55 }}>
            {measurementItems.map((item) => (
              <Box
                key={`${item.measurement_state}:${item.count}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.55,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>
                  {measurementStateLabel(item.measurement_state)}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#7c3aed', flexShrink: 0 }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Impact tracking will appear here once patterns are reviewed and move through action readiness.
          </Typography>
        )}
      </Box>
      <Box sx={{ mt: 1.2, pt: 1.1, borderTop: '1px solid', borderColor: alpha('#d97706', 0.18) }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.25 }}>
          Action workflow
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, mb: 0.85, lineHeight: 1.35, fontSize: 11.5 }}>
          {actionSnapshotLine}
        </Typography>
        {actionSnapshotItems.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 0.55 }}>
            {actionSnapshotItems.map((item) => (
              <Box
                key={`${item.action_state}:${item.count}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.55,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#e2e8f0', 0.95),
                  backgroundColor: alpha('#ffffff', 0.88),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12.5, minWidth: 0 }}>
                  {actionSnapshotStateLabel(item.action_state)}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#065f46', flexShrink: 0 }}>
                  {item.count}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Action workflow will appear here once owners, work orders, or next review times are recorded.
          </Typography>
        )}
      </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );

  if (groupedDrilldown) {
    return (
      <Box data-testid="recurrence-drilldown" sx={surfaceSx}>
        {content}
      </Box>
    );
  }

  return (
    <Paper variant="outlined" data-testid="recurring-risk-card" sx={surfaceSx}>
      {content}
    </Paper>
  );
};

export default HotspotsCard;
